/**
 * useContactSuggestions — the network half of the Focus-people typeahead.
 *
 * ChipInput is presentational (no session/network); this hook owns the lookup so the contact search
 * stays out of the chip editor. It mirrors the AttendeeChips contact-suggestion core: useSession()
 * for the token, a 300ms debounce, a min-2-char gate, and a mount-guard ref so a late async result
 * never setState after unmount (e.g. the customize drawer closed mid-type).
 *
 * Each contact is mapped to a { label, sublabel, value } row for the dropdown. emails are canonical
 * `{ value }[]` but elements may be plain strings, so they are ALWAYS unwrapped via
 * extractContactValue — rendering a raw `{ value }` object as a React child throws "Objects are not
 * valid as a React child" (#4906). The chip stores the human-readable NAME (the prompt compiler
 * quotes focus people as data), so `value` is the name, falling back to the email when unnamed.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useSession } from "@/features/auth/hooks/useSession";
import { listObjects } from "@/lib/api/endpoints/objects";
import { extractContactValue } from "@/modules/contacts/contactValue";
import type { ChipSuggestion } from "./ChipInput";

export function useContactSuggestions(): {
  suggestions: ChipSuggestion[];
  onQueryChange: (query: string) => void;
} {
  const { token } = useSession();
  const [suggestions, setSuggestions] = useState<ChipSuggestion[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Guards async results against setState-after-unmount: closing the drawer while a debounced
  // lookup is in-flight must not crash (mirrors AttendeeChips #4197).
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const search = useCallback(
    async (query: string) => {
      if (!token || query.trim().length < 2) {
        if (isMountedRef.current) setSuggestions([]);
        return;
      }
      try {
        const result = await listObjects(token, { type: "contact", query, limit: 10 });
        if (!isMountedRef.current) return;
        const items = (result?.items ?? [])
          .map((c: any): ChipSuggestion => {
            // emails/email may be a canonical `{ value }` element or a plain string — always unwrap.
            const email =
              extractContactValue(c.emails?.[0]) || extractContactValue(c.email);
            const name = String(c.title || c.name || "").trim();
            const value = name || email;
            return { label: name || email, sublabel: email || undefined, value };
          })
          .filter((s) => !!s.value);
        setSuggestions(items);
      } catch (err) {
        console.error("[useContactSuggestions] Search failed:", err);
        if (isMountedRef.current) setSuggestions([]);
      }
    },
    [token],
  );

  const onQueryChange = useCallback(
    (query: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => search(query), 300);
    },
    [search],
  );

  return { suggestions, onQueryChange };
}
